// ─────────────────────────────────────────────────────────────────────────────
// Data Package / Result Validation — the PRE-SUBMIT REVIEW GATES.
//
// Legacy makes the reviewer complete a ritual before a batch may be submitted,
// validated or approved: tick a row in Batch Results (QBIDSubmit :3186
// "selectchkbox"), review the Check Points (:3203) and select the Review Log
// (:3192). Alpaca Intelligence adds the category rule on top — Sample Results is
// mandatory, the other categories prompt softly.
//
// This module is the PURE decision layer, kept dependency-free for the same
// reason as `resultCategory.ts`: so the rules can be unit-tested without
// rendering the 4k-line SdmsPackageDetail grid (see reviewSelectionGate.test.ts).
// SdmsPackageDetail composes these three in order; it owns only the side effects
// (notify / confirm / tab jump).
//
// WHY IT EXISTS AT ALL. The gate used to live inline, TWICE — once for the
// popup's Submit and once, copy-pasted, for the full-page Validate/Approve. That
// duplication is exactly how the PLM branch came to be missing from one of them,
// and the inline version had no test. Both failures below were reported by the
// owner on 2026-09-25 ("Submit not working / after click submit all grid rows are
// disappeared"):
//
//   1. WRONG COLLECTION. A PLM package has no Sample / QC / Surrogate /
//      Internal Standard tabs — its results are the ONE "PLM Results" grid. The
//      gate nonetheless walked the SDMS rows keyed by `batchResultRowKey`, while
//      a ticked PLM row lives in the SAME checked-set under its `sequenceOid`.
//      The two key spaces never intersect, so a fully-ticked PLM package still
//      failed the "at least one Sample Results row" gate — unclearable.
//   2. WRONG RECOVERY. On failing, it sent the user to the "sample" tab, which a
//      PLM package does not render — so the grid looked as though it had emptied
//      itself.
//
// So a PLM package gets the SAME RULE against the grid it actually shows.
//
// A THIRD failure surfaced the moment the first two were fixed (owner report
// 2026-09-25, "where?"): Submit then demanded "You must review the check points
// before submission." on a package that renders NO Check Points tab — see
// MarkInput.checkPointsApplicable below.
// ─────────────────────────────────────────────────────────────────────────────
import type { ResultCategory } from './resultCategory';

/** The tab a blocked gate sends the reviewer to. `'plm'` is the PLM Results
 *  grid; the rest are the SDMS category tabs. */
export type ReviewTab = ResultCategory | 'plm';

/** A blocking gate's verdict: pass, or the message to show plus where to go. */
export type GateVerdict =
  | { blocked: false }
  | { blocked: true; message: string; goTo?: ReviewTab };

const PASS: GateVerdict = { blocked: false };

/** Per-category counts of the rows the package holds. */
export type CategoryCounts = Record<ResultCategory, number>;

/** What the RESULT-SELECTION gate needs to know. */
export type SelectionInput = {
  /** True for a PLM package (a PLM test's batch) — switches which grid is read. */
  isPlm: boolean;
  /** Any row ticked at all, in whichever grid is shown (legacy's `mark.br`). */
  anyRowChecked: boolean;
  /** PLM: how many rows the user is ALLOWED to review, and how many they ticked.
   *  A package with nothing reviewable cannot be gated on a tick. */
  plmReviewableCount: number;
  plmCheckedCount: number;
  /** SDMS: rows per category, and whether each category has a ticked row. */
  categoryCounts: CategoryCounts;
  categorySelected: (key: ResultCategory) => boolean;
};

/**
 * Gate 1 + 2 — the reviewer has selected the result row(s) they are signing off.
 *
 * Order matters and matches legacy: "nothing ticked at all" is reported before
 * the more specific "nothing ticked in the mandatory grid", so the first message
 * a reviewer sees is the general one.
 */
export function resultSelectionGate(input: SelectionInput): GateVerdict {
  if (!input.anyRowChecked) {
    return { blocked: true, message: 'Please select an item to proceed further.' };
  }

  if (input.isPlm) {
    // Only gate when there IS something reviewable: a package whose every row is
    // already validated (or is the reviewer's own work under "No Self-Review")
    // offers nothing to tick, and must not be made unsubmittable by that.
    if (input.plmReviewableCount > 0 && input.plmCheckedCount === 0) {
      return {
        blocked: true,
        message: 'Please select at least one PLM Results row to proceed.',
        goTo: 'plm',
      };
    }
    return PASS;
  }

  // Sample Results is MANDATORY (client rule 2026-09-07: "one of the sample
  // results row is mandatory") — you cannot validate on QC / Surrogate /
  // Internal Standard selections alone. Enforced only when the package HAS
  // sample rows.
  if (input.categoryCounts.sample > 0 && !input.categorySelected('sample')) {
    return {
      blocked: true,
      message: 'Please select at least one Sample Results row to proceed.',
      goTo: 'sample',
    };
  }

  return PASS;
}

/**
 * The SOFT coverage prompt — which PRESENT categories have rows but none ticked.
 *
 * Sample is excluded: it is hard-required by {@link resultSelectionGate}. A PLM
 * package has no categories at all, so it yields none and is never prompted.
 * Non-blocking by design — the caller asks "…anyway?" and proceeds on yes.
 */
export function unreviewedCategories(
  input: Pick<SelectionInput, 'isPlm' | 'categoryCounts' | 'categorySelected'>,
  order: readonly ResultCategory[],
): ResultCategory[] {
  if (input.isPlm) return [];
  return order.filter(
    (k) => k !== 'sample' && input.categoryCounts[k] > 0 && !input.categorySelected(k),
  );
}

/** What the CHECK-POINT / REVIEW-LOG gate needs to know. */
export type MarkInput = {
  /** A row is ticked in the Check Points grid. */
  checkPoints: boolean;
  /** A row is ticked in the Review Log grid. */
  reviewLog: boolean;
  /**
   * Does this package have check points that CAN be reviewed? Default true.
   *
   * Pass false when there is nothing to tick, for either of two reasons — both
   * of which made this gate unsatisfiable in practice (owner reports 2026-09-25):
   *
   *   1. NO TAB. A PLM package deliberately renders no Check Points tab (owner
   *      decision 2026-09-24, legacy parity — the legacy PLM popup has none
   *      either), and the grid renders ONLY under that tab. A reviewer who had
   *      ticked every PLM row was told "You must review the check points before
   *      submission." with no check points anywhere on screen — hence the
   *      one-word reply, "where?".
   *   2. NO ROWS. On the full-page SDMS Result Validation the tab IS shown, but
   *      the grid can be empty: check points are seeded onto a package from the
   *      `dbo.DataPackageCheckList` master, and a lab that has configured none
   *      gets a package with zero `dbo.DataPackageChecklistQC` rows. Verified on
   *      AGE_ALPACALIMS_Test, where BOTH tables are empty — so not one batch in
   *      that database, PLM or SDMS, could be validated.
   *
   * Either way the reviewer is asked to complete a step the screen cannot offer,
   * which is a deadlock rather than a slow path. A lab that HAS configured check
   * points has rows, so the gate applies to it exactly as before — this weakens
   * nothing in a configured environment.
   *
   * It also matches the server, whose validation in
   * AI_Analysis_Sdms_DataPackage_Transition_SP is a pair of `IF EXISTS` checks
   * over those same rows — vacuous when there are none (confirmed by a live
   * dry-run with `@ValidateCheckpoints = 1`, the value the client sends).
   *
   * Same shape as the PLM selection rule above, which likewise only gates when
   * `plmReviewableCount > 0`: never demand a review of an empty set.
   */
  checkPointsApplicable?: boolean;
};

/**
 * Gates 3 + 4 — the Check Points were reviewed and the Review Log was selected.
 *
 * The Review Log gate is NOT conditional: a PLM popup renders that grid at the
 * bottom beside Attachments, and every package on the live database carries at
 * least one review-log row (5 of 5 checked), so it is a real, tickable step.
 * The Check Points gate applies only where there is something to tick — see
 * {@link MarkInput.checkPointsApplicable}.
 */
export function reviewMarkGate(marks: MarkInput): GateVerdict {
  if ((marks.checkPointsApplicable ?? true) && !marks.checkPoints) {
    return { blocked: true, message: 'You must review the check points before submission.' };
  }
  if (!marks.reviewLog) {
    return { blocked: true, message: 'Select the review log to Submit.' };
  }
  return PASS;
}
